import React, { useState, useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from './widgets/chartInit';
import { getThemeConfig } from './widgets/chartTheme';
import { ChartErrorBoundary, ChartErrorIcon, TickerInfoHeader } from './widgets/chartComponents';
import { fetchWidgetArtifact } from '../utils/widgetApi';

function applyTheme(chartSpec) {
    const isDarkMode = document.documentElement.classList.contains('dark');
    const theme = getThemeConfig(isDarkMode);
    const merged = Highcharts.merge(theme, chartSpec);

    if (merged.yAxis) {
        const axes = Array.isArray(merged.yAxis) ? merged.yAxis : [merged.yAxis];
        const numPanels = axes.length;
        const requiredHeight = 300 + (numPanels - 1) * 50;

        if (!merged.chart) {
            merged.chart = {};
        }
        if (!merged.chart.height || merged.chart.height < requiredHeight) {
            merged.chart.height = requiredHeight;
        }
    }

    if (merged.yAxis) {
        const isArray = Array.isArray(merged.yAxis);
        const axes = isArray ? merged.yAxis.filter(axis => axis != null) : [merged.yAxis];
        const lineColor = isDarkMode ? '#282828' : '#e5e7eb';

        axes.forEach((axis) => {
            axis.labels = axis.labels || {};
            axis.labels.style = {
                ...(axis.labels.style || {}),
                color: isDarkMode ? '#9ca3af' : '#6b7280',
            };
            axis.gridLineWidth = 0;
            axis.lineColor = lineColor;
            axis.lineWidth = 0;

            if (axes.length === 1) {
                axis.title = { text: null };
            }
        });

        merged.yAxis = isArray ? axes : axes[0];
    }

    return merged;
}

const ChartBlock = ({ block }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartMetadata, setChartMetadata] = useState(null);
    const [tickerInfoData, setTickerInfoData] = useState([]);

    useEffect(() => {
        const fetchChartData = async () => {
            if (!block?.artifact_id) {
                setError('No artifact ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const artifact = await fetchWidgetArtifact(block.artifact_id, true);
                const chartSpec = artifact.data || artifact;
                const metadata = chartSpec.metadata || artifact.metadata || null;
                setChartMetadata(metadata);
                setChartData(applyTheme(chartSpec));
            } catch (err) {
                console.error('Error fetching chart data:', err);
                setError(err.message || 'Failed to load chart');
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, [block?.artifact_id]);

    useEffect(() => {
        const symbols = chartMetadata?.symbols;
        const tickers = Array.isArray(symbols) ? symbols : (symbols ? [symbols] : []);

        if (tickers.length === 0) {
            setTickerInfoData([]);
            return;
        }

        const fetchTickerInfo = async () => {
            try {
                const apiBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'https://api.simplize.dev';
                const promises = tickers.map(ticker =>
                    fetch(`${apiBaseUrl}/api/company/summary/${ticker}`)
                        .then(res => {
                            if (!res.ok) throw new Error(`Failed to fetch ${ticker}`);
                            return res.json();
                        })
                        .then(response => {
                            const actualData = response.data || response;
                            return { ticker, data: actualData, error: null };
                        })
                        .catch(err => ({ ticker, data: null, error: err.message }))
                );
                const results = await Promise.all(promises);
                setTickerInfoData(results);
            } catch (err) {
                console.error('Error fetching ticker info:', err);
            }
        };

        fetchTickerInfo();
    }, [chartMetadata?.symbols?.join(',')]);

    const isDarkMode = document.documentElement.classList.contains('dark');

    const tickers = (() => {
        const symbols = chartMetadata?.symbols;
        return Array.isArray(symbols) ? symbols : (symbols ? [symbols] : []);
    })();

    if (loading) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: 'var(--bg-secondary, #f5f5f5)',
                borderRadius: '8px',
                marginTop: '12px',
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid var(--border-color, #e0e0e0)',
                    borderTopColor: 'var(--primary-color, #4f46e5)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto',
                }} />
                <p style={{
                    marginTop: '16px',
                    color: 'var(--text-secondary, #666)',
                    fontSize: '14px',
                }}>
                    Loading chart...
                </p>
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #f3f4f6',
                marginTop: '12px',
            }}>
                <ChartErrorIcon isDarkMode={isDarkMode} />
                <p style={{
                    marginTop: '16px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    fontSize: '14px',
                }}>
                    Có lỗi khi tải biểu đồ
                </p>
            </div>
        );
    }

    return (
        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
            {block.title && (
                <h3 style={{
                    marginBottom: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isDarkMode ? '#e5e5e5' : '#202124',
                    letterSpacing: '-0.01em',
                }}>
                    {block.title}
                </h3>
            )}

            <div style={{
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #f3f4f6',
                overflow: 'hidden',
            }}>
                <TickerInfoHeader
                    tickers={tickers}
                    tickerInfoData={tickerInfoData}
                    tickerInfoLoading={tickerInfoData.length === 0 && tickers.length > 0}
                    isDarkMode={isDarkMode}
                />

                <ChartErrorBoundary>
                    <style>{`
                        .chart-wrapper .highcharts-exporting-group {
                            opacity: 0;
                            transition: opacity 0.2s ease-in-out;
                        }
                        .chart-wrapper:hover .highcharts-exporting-group {
                            opacity: 1;
                        }
                    `}</style>
                    <div className="chart-wrapper" style={{
                        backgroundColor: 'transparent',
                        padding: '16px',
                    }}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            constructorType={'stockChart'}
                            options={chartData}
                        />
                    </div>
                </ChartErrorBoundary>
            </div>
        </div>
    );
};

export default ChartBlock;
